import { html, unsafeCSS } from "lit";
import { customElement, property } from "lit/decorators.js";
import { createRef, ref, type Ref } from "lit/directives/ref.js";
import { cn } from "@/utilities/css";
import { EkoElement } from "@/utilities/element";
import style from "./form.css?inline";

const INPUT_ELEMENTS =
  "input, select, textarea, eko-toggle, eko-checkbox, eko-radio-group, eko-select, eko-select-advanced, eko-textarea, eko-input";

export interface FormDataResult {
  data: Record<string, any>;
  isValid: boolean;
  errors: Record<string, string>;
  formData: globalThis.FormData;
}

@customElement("eko-form")
export class Form extends EkoElement {
  static formAssociated = true;
  static styles = [...EkoElement.styles, unsafeCSS(style)];

  private formId: string;
  private formRef: Ref<HTMLFormElement> = createRef();

  private handleControlChange = () => {
    this.dispatchEvent(new Event("change", { bubbles: true }));
  };
  private handleControlInput = () => {
    this.dispatchEvent(new Event("input", { bubbles: true }));
  };

  private handleControlInvalid = () => {
    this.dispatchEvent(new Event("invalid", { bubbles: true }));
  };

  private handleReset = (event: Event) => {
    event.preventDefault();
    this.reset();
    this.dispatchEvent(new Event("reset", { bubbles: true }));
  };

  private handleResetButtonClick = (event: Event) => {
    event.preventDefault();
    this.reset();
  };

  private handleSlotChange = () => {
    this.setupFormControls();
  };

  private handleSubmit = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();

    const isValid = this.checkValidity();
    const result = this.getFormData();

    if (isValid && !this.novalidate) {
      // Dispatch submit event with form data
      const submitEvent = new CustomEvent("submit", {
        bubbles: true,
        cancelable: true,
        detail: result,
      });

      const dispatched = this.dispatchEvent(submitEvent);

      // If event wasn't prevented and we have an action, submit natively
      if (dispatched && this.action) {
        this.nativeSubmit();
      }
    } else if (!isValid) {
      // Report validation errors
      this.reportValidity();

      // Dispatch submit event even for invalid forms (like native forms)
      this.dispatchEvent(
        new CustomEvent("submit", {
          bubbles: true,
          cancelable: true,
          detail: result,
        })
      );
    }
  };

  private handleSubmitButtonClick = (event: Event) => {
    event.preventDefault();

    if (this.formRef.value) {
      this.formRef.value.requestSubmit();
    }
  };

  private internals: ElementInternals;

  @property({ type: String })
  acceptcharset: string = "";

  @property({ type: String })
  action: string = "";

  @property({ type: String })
  autocomplete: "on" | "off" = "on";

  @property({ type: String })
  class: string = "";

  @property({ type: String })
  enctype: "application/x-www-form-urlencoded" | "multipart/form-data" | "text/plain" =
    "application/x-www-form-urlencoded";

  @property({ type: String })
  method: "get" | "post" = "get";

  @property({ type: String })
  name: string = "";

  @property({ type: Boolean })
  novalidate: boolean = false;

  @property({ type: String })
  target: string = "";

  constructor() {
    super();
    this.internals = this.attachInternals();
    this.formId = `eko-form-${Math.random().toString(36).substr(2, 9)}`;
  }

  private cleanupEventListeners() {
    this.removeEventListener("slotchange", this.handleSlotChange);

    // Clean up event listeners on form controls
    const formInputs = this.querySelectorAll(INPUT_ELEMENTS) as NodeListOf<any>;
    formInputs.forEach((input: any) => {
      input.removeEventListener("input", this.handleControlInput);
      input.removeEventListener("change", this.handleControlChange);
      input.removeEventListener("invalid", this.handleControlInvalid);
    });
  }

  /**
   * Native form submission (when action is specified)
   */
  private nativeSubmit() {
    if (this.formRef.value && this.action) {
      const form = this.formRef.value;
      const formData = new globalThis.FormData(form);

      // Add data from custom form controls
      const formInputs = this.querySelectorAll(INPUT_ELEMENTS) as NodeListOf<any>;
      formInputs.forEach((input: any) => {
        if (input.name && input.value !== undefined) {
          if (
            (input.type === "checkbox" || input.tagName === "EKO-TOGGLE") &&
            !input.checked &&
            !input.binary
          ) {
            return; // Don't include unchecked checkboxes/toggles unless binary mode
          }
          formData.set(input.name, input.value);
        }
      });

      // Submit based on method
      if (this.method === "get") {
        const url = new URL(this.action, globalThis.window.location.href);
        for (const [key, value] of formData.entries()) {
          url.searchParams.append(key, value as string);
        }
        if (this.target) {
          globalThis.window.open(url.toString(), this.target);
        } else {
          globalThis.window.location.href = url.toString();
        }
      } else {
        // POST submission
        const tempForm = document.createElement("form");
        tempForm.action = this.action;
        tempForm.method = this.method;
        tempForm.enctype = this.enctype;
        if (this.target) tempForm.target = this.target;
        tempForm.style.display = "none";

        for (const [key, value] of formData.entries()) {
          const input = document.createElement("input");
          input.name = key;
          input.value = value as string;
          tempForm.appendChild(input);
        }

        document.body.appendChild(tempForm);
        tempForm.submit();
        document.body.removeChild(tempForm);
      }
    }
  }

  private setupEventListeners() {
    // Handle slotted form controls
    this.addEventListener("slotchange", this.handleSlotChange);

    // Set up mutation observer to watch for dynamically added form controls
    const observer = new globalThis.MutationObserver(() => {
      this.setupFormControls();
    });

    observer.observe(this, { childList: true, subtree: true });
    this.setupFormControls();
  }

  private setupFormControls() {
    // Find all form controls and set up event listeners
    const formControls = this.querySelectorAll(INPUT_ELEMENTS);

    formControls.forEach((control: any) => {
      // Remove existing listeners to avoid duplicates
      control.removeEventListener("input", this.handleControlInput);
      control.removeEventListener("change", this.handleControlChange);
      control.removeEventListener("invalid", this.handleControlInvalid);

      // Add event listeners
      control.addEventListener("input", this.handleControlInput);
      control.addEventListener("change", this.handleControlChange);
      control.addEventListener("invalid", this.handleControlInvalid);

      // Associate control with this form if it supports form association
      if (control.formId === undefined && control.form === undefined) {
        control.setAttribute("form", this.formId);
      }
    });

    // Set up submit buttons
    this.setupSubmitButtons();
  }

  private setupSubmitButtons() {
    const submitButtons = this.querySelectorAll(
      'button[type="submit"], input[type="submit"], eko-button[type="submit"]'
    );

    submitButtons.forEach((button: any) => {
      button.removeEventListener("click", this.handleSubmitButtonClick);
      button.addEventListener("click", this.handleSubmitButtonClick);

      // Associate button with form
      if (!button.hasAttribute("form")) {
        button.setAttribute("form", this.formId);
      }
    });

    const resetButtons = this.querySelectorAll('button[type="reset"], eko-button[type="reset"]');
    resetButtons.forEach((button: any) => {
      button.removeEventListener("click", this.handleResetButtonClick);
      button.addEventListener("click", this.handleResetButtonClick);
    });
  }

  /**
   * Check validity of the entire form
   */
  checkValidity(): boolean {
    if (this.novalidate) return true;

    let isValid = true;

    // Check native form validity
    if (this.formRef.value && !this.formRef.value.checkValidity()) {
      isValid = false;
    }

    // Check custom form controls
    const formInputs = this.querySelectorAll(INPUT_ELEMENTS) as NodeListOf<any>;
    formInputs.forEach((input: any) => {
      if (input.checkValidity && !input.checkValidity()) {
        isValid = false;
      }
    });

    // Update form validity state
    if (isValid) {
      this.internals.setValidity({});
    } else {
      this.internals.setValidity({ customError: true }, "Form contains validation errors");
    }

    return isValid;
  }

  connectedCallback() {
    super.connectedCallback();
    this.setupEventListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.cleanupEventListeners();
  }

  firstUpdated(changedProperties: Map<string, any>) {
    super.firstUpdated?.(changedProperties);
    this.setupEventListeners();
  }

  // Form lifecycle methods for form association
  formAssociatedCallback() {
    // Called when associated with a form
  }

  formDisabledCallback(disabled: boolean) {
    // Propagate disabled state to form controls
    const formInputs = this.querySelectorAll(INPUT_ELEMENTS) as NodeListOf<any>;
    formInputs.forEach((input: any) => {
      input.disabled = disabled;
    });
    const submitButtons = this.querySelectorAll('button[type="submit"], input[type="submit"]');
    submitButtons.forEach((button: any) => {
      button.disabled = disabled;
    });
    const resetButtons = this.querySelectorAll('button[type="reset"]');
    resetButtons.forEach((button: any) => {
      button.disabled = disabled;
    });
  }

  formResetCallback() {
    this.reset();
  }

  formStateRestoreCallback(state: string | null) {
    // Restore form state after navigation
    if (state) {
      try {
        const formState = JSON.parse(state);
        Object.entries(formState).forEach(([name, value]) => {
          const input = this.querySelector(`[name="${name}"]`) as any;
          if (input && "value" in input) {
            input.value = value;
          }
        });
      } catch (e) {
        console.warn("Failed to restore form state:", e);
      }
    }
  }

  /**
   * Get form data including values from custom form controls
   */
  getFormData(): FormDataResult {
    const data: Record<string, any> = {};
    const errors: Record<string, string> = {};
    const formData = new globalThis.FormData();

    // Get native form data first
    if (this.formRef.value) {
      const nativeFormData = new globalThis.FormData(this.formRef.value);
      for (const [key, value] of nativeFormData.entries()) {
        formData.set(key, value);
        data[key] = value;
      }
    }

    // Find all custom form controls with a name attribute
    const formInputs = this.querySelectorAll(INPUT_ELEMENTS) as NodeListOf<any>;
    let isValid = true;

    formInputs.forEach((input: any) => {
      const name = input.name;
      if (!name) return;

      // Get the value from the control
      let value = null;
      if (input.value !== undefined) {
        value = input.value;
      }

      // Handle checkboxes - only include if checked, unless binary mode
      if (input.type === "checkbox" || input.type === "eko-checkbox") {
        if (input.checked) {
          value = input.value || "on";
        } else if (input.binary) {
          // For binary checkboxes, include "0" when unchecked
          value = "0";
        } else {
          return;
        }
      }

      // Handle toggles - same logic as checkboxes
      if (input.tagName === "EKO-TOGGLE") {
        if (input.checked) {
          value = input.value || "on";
        } else if (input.binary) {
          // For binary toggles, include "0" when unchecked
          value = "0";
        } else {
          return;
        }
      }

      // Handle select components with multiple values (arrays)
      if (input.tagName === "EKO-SELECT-ADVANCED" && Array.isArray(value)) {
        // For multiple select, add each value as a separate form entry
        value.forEach((val: any) => {
          formData.append(name, val);
        });
        data[name] = value;
      }

      // Handle multiple controls with the same name (like checkbox groups)
      else if (data[name] !== undefined) {
        // Convert to array if not already
        if (!Array.isArray(data[name])) {
          data[name] = [data[name]];
        }
        data[name].push(value);
        formData.append(name, value);
      } else {
        data[name] = value;
        formData.set(name, value);
      }

      // Check validity
      if (input.checkValidity && !input.checkValidity()) {
        isValid = false;
        if (input.internals?.validity?.valid === false) {
          errors[name] =
            input.internals.validationMessage || input.validationMessage || "Invalid value";
        } else if (input.validationMessage) {
          errors[name] = input.validationMessage;
        }
      }
    });

    return { data, errors, formData, isValid };
  }

  render() {
    return html`
      <form
        ${ref(this.formRef)}
        id=${this.formId}
        class=${cn("eko-form", this.class)}
        part="form"
        action=${this.action}
        method=${this.method}
        enctype=${this.enctype}
        target=${this.target}
        ?novalidate=${this.novalidate}
        autocomplete=${this.autocomplete}
        name=${this.name}
        accept-charset=${this.acceptcharset}
        @submit=${this.handleSubmit}
        @reset=${this.handleReset}>
        <slot @slotchange=${this.handleSlotChange}></slot>
      </form>
    `;
  }

  /**
   * Report validity and show validation messages
   */
  reportValidity(): boolean {
    if (this.novalidate) return true;

    const isValid = this.checkValidity();

    if (!isValid) {
      // According to HTML5 spec, reportValidity should only show a popup for the
      // first invalid field and focus on it, not all invalid fields
      const formInputs = this.querySelectorAll(INPUT_ELEMENTS) as NodeListOf<any>;

      // Find the first invalid input in document order
      for (const input of formInputs) {
        if (input.checkValidity && !input.checkValidity()) {
          // Only report validity for the first invalid field
          if (input.reportValidity) {
            input.reportValidity();
          }
          // Focus the first invalid field
          if (input.focus) {
            input.focus();
          }
          break; // Stop after the first invalid field
        }
      }
    }

    return isValid;
  }

  /**
   * Request form submission (triggers validation)
   */
  requestSubmit(submitter?: HTMLElement): void {
    if (this.formRef.value) {
      this.formRef.value.requestSubmit(submitter);
    }
  }

  /**
   * Reset the form
   */
  reset(): void {
    // Reset native form
    if (this.formRef.value) {
      this.formRef.value.reset();
    }

    // Reset custom form controls
    const formControls = this.querySelectorAll("[name]") as NodeListOf<any>;
    formControls.forEach((control: any) => {
      if (control.formResetCallback) {
        control.formResetCallback();
      } else if ("value" in control) {
        // Reset to default value
        if (control.type === "checkbox" || control.type === "radio") {
          control.checked = control.hasAttribute("checked");
        } else {
          control.value = control.getAttribute("value") || "";
        }
      }
    });

    // Clear validation state
    this.internals.setValidity({});
  }

  /**
   * Submit the form programmatically
   */
  submit(): void {
    if (this.formRef.value) {
      this.formRef.value.requestSubmit();
    }
  }

  // Getter properties to match native form API
  get elements(): globalThis.HTMLFormControlsCollection {
    return this.formRef.value?.elements || ([] as any);
  }

  get form(): HTMLFormElement | null {
    return this.formRef.value || null;
  }

  get length(): number {
    return this.elements.length;
  }

  get validationMessage(): string {
    return this.internals.validationMessage;
  }

  get validity(): globalThis.ValidityState {
    return this.internals.validity;
  }

  get willValidate(): boolean {
    return !this.novalidate;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-form": Form;
  }
}
